/* eslint-env browser */
// Settings › Driver incentives (wave 3, W2_Portal_Settings look): the Driver
// Weekly Goal, kept at /settings/driverWeeklyGoal. Drivers see a progress
// bar on the trips screen and the bonus is credited automatically (top-up
// balance by default, earning wallet by setting) when they cross a tier.
//
// Save writes exactly what the old Save wrote: the WHOLE settings object
// with driverWeeklyGoal = { enabled, mode, bonusDestination, tiers } through
// editSettings, the tiers cleaned the same way (numbers, invalid rows
// dropped, one row per threshold, sorted). Only with critical edits on.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { api } from 'common';
import CircularLoading from '../components/CircularLoading';
import { showToast, confirmDialog, InlineMessage } from '../components/feedback';
import { tokens as c } from '../theme';
import { SectionCard, SettingRow, SaveBar } from '../components/modern';

// The goal saved here survives switching Settings tabs for the rest of the
// session while the store still holds the settings it started from.
let savedCache = null; // { source, goal }

const DEFAULT_TIER = { tripsRequired: 9, revenueRequired: 5000, bonusAmount: 500 };

// Wide screens (lg and up): the switch and the two choices in the first
// column, the tiers table beside them; below lg everything stacks as before.
// While stacked, the first column keeps the line under its last row
// (SettingRow drops it on a last child); in a column it ends clean.
const PAIR_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 4,
  alignItems: 'start',
};
const GROUP_SX = {
  minWidth: 0,
  '& > [id$="-row"]:last-child': { borderBottom: { xs: `1px solid ${c.OUTLINE}`, lg: 0 } },
};

// The form state for a stored goal (defaults when there is none), as before.
const formFrom = (existing) => {
  if (existing && typeof existing === 'object') {
    return {
      enabled: existing.enabled === true,
      mode: existing.mode === 'revenue' ? 'revenue' : 'count',
      bonusDestination: existing.bonusDestination === 'wallet' ? 'wallet' : 'topUp',
      // Tiers carry BOTH thresholds: only the active mode's is read at award
      // time, the other is kept so the mode can flip without losing it.
      tiers:
        Array.isArray(existing.tiers) && existing.tiers.length > 0
          ? existing.tiers.map((tier) => ({
              tripsRequired: Number(tier.tripsRequired) || 0,
              revenueRequired: Number(tier.revenueRequired) || 0,
              bonusAmount: Number(tier.bonusAmount) || 0,
            }))
          : [{ ...DEFAULT_TIER }],
    };
  }
  return { enabled: false, mode: 'count', bonusDestination: 'topUp', tiers: [{ ...DEFAULT_TIER }] };
};

const formForSource = (source) => {
  if (!source) return null;
  if (savedCache && savedCache.source === source) return formFrom(savedCache.goal);
  return formFrom(source.driverWeeklyGoal);
};

const DriverIncentives = () => {
  const { t, i18n } = useTranslation();
  const { editSettings, clearSettingsViewError } = api;
  const settingsdata = useSelector((state) => state.settingsdata);
  const dispatch = useDispatch();
  const source = settingsdata.settings;

  const [base, setBase] = useState(() => formForSource(source));
  const [data, setData] = useState(() => formForSource(source));
  const [clicked, setClicked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tiersError, setTiersError] = useState(null);
  const sourceRef = useRef(source);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (source && source !== sourceRef.current) {
      sourceRef.current = source;
      setBase(formForSource(source));
      setData(formForSource(source));
    }
  }, [source]);

  const changed = useMemo(() => {
    if (!data || !base) return [];
    const out = [];
    if (data.enabled !== base.enabled) out.push('enabled');
    if (data.bonusDestination !== base.bonusDestination) out.push('bonusDestination');
    if (data.mode !== base.mode) out.push('mode');
    if (JSON.stringify(data.tiers) !== JSON.stringify(base.tiers)) out.push('tiers');
    return out;
  }, [data, base]);

  const handleTierChange = (idx, field, value) => {
    const next = [...data.tiers];
    next[idx] = { ...next[idx], [field]: value };
    setData({ ...data, tiers: next });
    setTiersError(null);
  };

  const handleAddTier = () => {
    setData({ ...data, tiers: [...data.tiers, { tripsRequired: 0, revenueRequired: 0, bonusAmount: 0 }] });
  };

  const handleRemoveTier = (idx) => {
    // Always keep at least one row so the feature is never switched off by
    // deleting everything.
    if (data.tiers.length <= 1) return;
    setData({ ...data, tiers: data.tiers.filter((_, i) => i !== idx) });
  };

  const discard = async () => {
    if (!changed.length || saving) return;
    const ok = await confirmDialog({
      title:
        changed.length === 1
          ? t('settings_discard_title_one', { defaultValue: 'Discard 1 unsaved change?' })
          : t('settings_discard_title_many', { n: changed.length, defaultValue: 'Discard {{n}} unsaved changes?' }),
      body: t('settings_discard_body', { defaultValue: 'The settings go back to how they were when you last saved.' }),
      confirmLabel: t('settings_discard', { defaultValue: 'Discard' }),
      destructive: true,
    });
    if (ok && aliveRef.current) {
      setData(base);
      setTiersError(null);
    }
  };

  // Validate and write to /settings: tiers as numbers, invalid rows dropped,
  // one row per threshold of the active mode (last wins), sorted ascending.
  // Both thresholds are kept on every tier; only the active mode's field
  // must be above 0.
  const handleSubmit = async () => {
    if (!data || saving) return;
    if (!settingsdata.settings?.AllowCriticalEditsAdmin) {
      showToast.warning(t('demo_mode'));
      return;
    }

    const mode = data.mode === 'revenue' ? 'revenue' : 'count';
    const activeField = mode === 'revenue' ? 'revenueRequired' : 'tripsRequired';

    const cleaned = (data.tiers || [])
      .map((tier) => ({
        tripsRequired: Math.floor(Number(tier.tripsRequired) || 0),
        revenueRequired: Number(tier.revenueRequired) || 0,
        bonusAmount: Number(tier.bonusAmount) || 0,
      }))
      .filter((tier) => (Number(tier[activeField]) || 0) > 0 && tier.bonusAmount > 0);

    if (data.enabled && cleaned.length === 0) {
      const msg =
        mode === 'revenue'
          ? t('driver_incentives_need_one_tier_revenue', { defaultValue: 'Add at least one tier with revenue > 0 and bonus > 0.' })
          : t('driver_incentives_need_one_tier', { defaultValue: 'Add at least one tier with trips > 0 and bonus > 0.' });
      setTiersError(msg);
      showToast.error(msg);
      return;
    }

    // One tier per threshold of the active mode (last write wins), sorted.
    const dedup = {};
    for (const tier of cleaned) {
      dedup[String(tier[activeField])] = tier;
    }
    const tiers = Object.values(dedup).sort((a, b) => (Number(a[activeField]) || 0) - (Number(b[activeField]) || 0));

    const goal = {
      enabled: !!data.enabled,
      mode: mode,
      bonusDestination: data.bonusDestination === 'wallet' ? 'wallet' : 'topUp',
      tiers,
    };
    const updatedSettings = { ...settingsdata.settings, driverWeeklyGoal: goal };
    setClicked(true);
    setSaving(true);
    try {
      await dispatch(editSettings(updatedSettings));
      savedCache = { source: sourceRef.current, goal };
      if (!aliveRef.current) return;
      const next = formFrom(goal);
      setBase(next);
      setData(next);
      showToast.success(t('settings_updated', { defaultValue: 'Settings updated' }));
    } catch (e) {
      if (aliveRef.current) showToast.error(t('update_failed', { defaultValue: 'Update failed' }));
    } finally {
      if (aliveRef.current) setSaving(false);
    }
  };

  const handleClose = () => {
    setClicked(false);
    dispatch(clearSettingsViewError());
  };

  if (!data) return <CircularLoading inline />;

  const thresholdField = data.mode === 'revenue' ? 'revenueRequired' : 'tripsRequired';
  const radioRow = (id, value, onChange, options) => (
    <RadioGroup id={id} row value={value} onChange={onChange} sx={{ columnGap: 2 }}>
      {options.map((o) => (
        <FormControlLabel key={o.value} value={o.value} control={<Radio />} label={o.label} sx={{ mr: 0 }} />
      ))}
    </RadioGroup>
  );

  return (
    <Box dir={i18n.dir()}>
      <SectionCard
        id="incentives"
        icon={<EmojiEventsOutlinedIcon />}
        hue="amber"
        title={t('driver_incentives', { defaultValue: 'Driver incentives' })}
        sx={{ display: 'flex', flexDirection: 'column', pb: 0 }}
      >
        {settingsdata.error && settingsdata.error.flag && clicked ? (
          <InlineMessage kind="error" onClose={handleClose} sx={{ mt: 1.5 }}>
            {t('update_failed', { defaultValue: 'Update failed' })}
          </InlineMessage>
        ) : null}
        <Box sx={{ mt: 1, pb: 1, ...PAIR_SX }}>
          <Box sx={GROUP_SX}>
              <SettingRow
                id="incentives-enabled"
                layout="switch"
                label={t('driver_incentives_enable', { defaultValue: 'Enable driver weekly goal' })}
                hint={t('driver_incentives_help', {
                  defaultValue:
                    'Set weekly trip targets. When a driver crosses a tier, the bonus is auto-credited. Trip counts reset every Monday (Addis Ababa time); the monthly bonus tally resets on the 1st.',
                })}
                changed={changed.includes('enabled')}
                control={
                  <Switch
                    id="incentives-enabled"
                    checked={!!data.enabled}
                    onChange={(e) => setData({ ...data, enabled: e.target.checked })}
                    inputProps={{ 'aria-describedby': 'incentives-enabled-hint' }}
                    sx={{ my: -0.75 }}
                  />
                }
              />
              <SettingRow
                id="incentives-dest"
                layout="stack"
                label={t('bonus_destination', { defaultValue: 'Bonus credit destination' })}
                changed={changed.includes('bonusDestination')}
                control={radioRow('incentives-dest', data.bonusDestination, (e) => setData({ ...data, bonusDestination: e.target.value }), [
                  { value: 'topUp', label: t('bonus_destination_topup', { defaultValue: 'Top-up wallet (default)' }) },
                  { value: 'wallet', label: t('bonus_destination_wallet', { defaultValue: 'Earning wallet' }) },
                ])}
              />
              {/* Goal type: which figure the weekly tier checks use. Both
                  thresholds stay on every tier, so switching loses nothing. */}
              <SettingRow
                id="incentives-mode"
                layout="stack"
                label={t('goal_type', { defaultValue: 'Goal type' })}
                hint={
                  data.mode === 'revenue'
                    ? t('goal_type_revenue_help', { defaultValue: 'Driver earns each tier when their weekly trip revenue (sum of gross trip costs before commission) crosses the threshold.' })
                    : t('goal_type_count_help', { defaultValue: 'Driver earns each tier when their completed trip count crosses the threshold.' })
                }
                changed={changed.includes('mode')}
                control={radioRow('incentives-mode', data.mode || 'count', (e) => setData({ ...data, mode: e.target.value === 'revenue' ? 'revenue' : 'count' }), [
                  { value: 'count', label: t('goal_type_count', { defaultValue: 'Count of completed trips' }) },
                  { value: 'revenue', label: t('goal_type_revenue', { defaultValue: 'Total trip revenue (pre-commission)' }) },
                ])}
              />
          </Box>
          <Box sx={{ minWidth: 0 }}>
              <SettingRow
                id="incentives-tiers"
                layout="stack"
                label={t('weekly_tiers', { defaultValue: 'Weekly tiers' })}
                hint={t('tier_help', {
                  defaultValue:
                    'Tiers are sorted ascending and awarded independently — a driver who completes 15 trips in one week earns every tier ≤ 15. Each tier pays out only once per week.',
                })}
                changed={changed.includes('tiers')}
                error={tiersError}
                control={
                  <Box sx={{ border: `1px solid ${c.OUTLINE}`, borderRadius: '10px', overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 420 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {data.mode === 'revenue'
                              ? t('tier_revenue', { defaultValue: 'Revenue required' })
                              : t('tier_trips', { defaultValue: 'Trips required' })}
                          </TableCell>
                          <TableCell>{t('tier_bonus', { defaultValue: 'Bonus amount' })}</TableCell>
                          <TableCell align="right" sx={{ width: 64 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.tiers.map((tier, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <TextField
                                type="number"
                                size="small"
                                fullWidth
                                value={tier[thresholdField]}
                                onChange={(e) => handleTierChange(idx, thresholdField, e.target.value)}
                                inputProps={
                                  data.mode === 'revenue'
                                    ? { min: 1, step: 100, inputMode: 'decimal', 'aria-label': `${t('tier_revenue', { defaultValue: 'Revenue required' })} ${idx + 1}` }
                                    : { min: 1, step: 1, inputMode: 'numeric', 'aria-label': `${t('tier_trips', { defaultValue: 'Trips required' })} ${idx + 1}` }
                                }
                                sx={{ '& input': { fontVariantNumeric: 'tabular-nums' } }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                size="small"
                                fullWidth
                                value={tier.bonusAmount}
                                onChange={(e) => handleTierChange(idx, 'bonusAmount', e.target.value)}
                                inputProps={{ min: 1, step: 1, inputMode: 'decimal', 'aria-label': `${t('tier_bonus', { defaultValue: 'Bonus amount' })} ${idx + 1}` }}
                                sx={{ '& input': { fontVariantNumeric: 'tabular-nums' } }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={t('incentives_remove_tier', { defaultValue: 'Remove tier' })}>
                                <span>
                                  <IconButton
                                    onClick={() => handleRemoveTier(idx)}
                                    disabled={data.tiers.length <= 1}
                                    aria-label={`${t('incentives_remove_tier', { defaultValue: 'Remove tier' })} ${idx + 1}`}
                                    sx={{ color: c.DANGER_TEXT }}
                                  >
                                    <DeleteOutlineRoundedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <Box sx={{ p: 1, borderTop: `1px solid ${c.OUTLINE}` }}>
                      <Button variant="text" startIcon={<AddRoundedIcon />} onClick={handleAddTier}>
                        {t('add_tier', { defaultValue: 'Add tier' })}
                      </Button>
                    </Box>
                  </Box>
                }
              />
          </Box>
        </Box>
        <SaveBar
          changedCount={changed.length}
          errorCount={tiersError ? 1 : 0}
          onJumpToError={() => {
            const el = document.getElementById('incentives-tiers-row');
            if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
          }}
          onDiscard={discard}
          onSave={handleSubmit}
          saving={saving}
          sx={{ mx: { xs: -2, md: -2.5 }, px: { xs: 2, md: 2.5 } }}
        />
      </SectionCard>
    </Box>
  );
};

export default DriverIncentives;
